// ── Kalshi RSA-PSS Request Signing ──────────────────────────────
// Native WebCrypto. Supports both PKCS#8 (BEGIN PRIVATE KEY) and
// PKCS#1 (BEGIN RSA PRIVATE KEY) — auto-converts PKCS#1 → PKCS#8
// via node-forge (lazy-loaded only when needed).
//
// Signature payload format (per Kalshi docs):
//   ${timestampMs}${METHOD}${path}
//   - No separators between fields
//   - Method is uppercase (GET, POST, DELETE)
//   - Path has leading slash, no trailing slash, no query params
//   - Timestamp is milliseconds since epoch as string

// ── Base URLs ───────────────────────────────────────────────────
// In dev, REST calls go through Vite's proxy to avoid CORS.
// In production, they go direct (assumes a reverse proxy or same-origin setup).

const isDev = import.meta.env.DEV;

const REST_URLS = {
  prod: isDev ? '/kalshi-api' : 'https://g1.newbx.com:9892/kalshi-api',
  demo: isDev ? '/kalshi-demo-api' : 'https://g1.newbx.com:9892/kalshi-demo-api',
} as const;

const WS_URLS = {
  prod: 'wss://api.elections.kalshi.com',
  demo: 'wss://demo-api.kalshi.co',
} as const;

export type KalshiEnvironment = 'prod' | 'demo';

export function getBaseUrl(env: KalshiEnvironment): string {
  return REST_URLS[env];
}

export function getWsUrl(env: KalshiEnvironment): string {
  return WS_URLS[env] + '/trade-api/ws/v2';
}

// ── PEM Parsing ─────────────────────────────────────────────────

function isPkcs1(pem: string): boolean {
  return pem.includes('BEGIN RSA PRIVATE KEY');
}

/** Convert PKCS#1 PEM → PKCS#8 DER via node-forge (lazy-loaded). */
async function convertPkcs1ToPkcs8(pem: string): Promise<ArrayBuffer> {
  const forge = await import('node-forge');
  const privateKey = forge.pki.privateKeyFromPem(pem);
  const asn1 = forge.pki.privateKeyToAsn1(privateKey);
  const info = forge.pki.wrapRsaPrivateKey(asn1);
  const der = forge.asn1.toDer(info);
  const bytes = new Uint8Array(der.length());
  for (let i = 0; i < der.length(); i++) {
    bytes[i] = der.at(i);
  }
  return bytes.buffer;
}

/** Decode PKCS#8 PEM → DER ArrayBuffer. */
function decodePkcs8Pem(pem: string): ArrayBuffer {
  const lines = pem
    .trim()
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l && !l.startsWith('-----'));

  const b64 = lines.join('');
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

export async function parsePem(pem: string): Promise<CryptoKey> {
  // Auto-detect format and get PKCS#8 DER
  const der = isPkcs1(pem)
    ? await convertPkcs1ToPkcs8(pem)
    : decodePkcs8Pem(pem);

  return crypto.subtle.importKey(
    'pkcs8',
    der,
    { name: 'RSA-PSS', hash: 'SHA-256' },
    false,
    ['sign'],
  );
}

// ── Request Signing ─────────────────────────────────────────────

function normalizePath(path: string): string {
  // Ensure leading slash, strip trailing slash (except root), strip query params
  let p = path.split('?')[0];
  if (!p.startsWith('/')) p = '/' + p;
  if (p.length > 1 && p.endsWith('/')) p = p.slice(0, -1);
  return p;
}

export async function sign(
  key: CryptoKey,
  timestamp: string,
  method: string,
  path: string,
): Promise<string> {
  const payload = `${timestamp}${method.toUpperCase()}${normalizePath(path)}`;
  const encoded = new TextEncoder().encode(payload);

  const signature = await crypto.subtle.sign(
    { name: 'RSA-PSS', saltLength: 32 },
    key,
    encoded,
  );

  // Standard base64 (not URL-safe)
  const bytes = new Uint8Array(signature);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

// ── Auth Headers ────────────────────────────────────────────────

export async function buildHeaders(
  accessKey: string,
  cryptoKey: CryptoKey,
  method: string,
  path: string,
): Promise<Record<string, string>> {
  const timestamp = String(Date.now());
  const signature = await sign(cryptoKey, timestamp, method, path);

  return {
    'KALSHI-ACCESS-KEY': accessKey,
    'KALSHI-ACCESS-TIMESTAMP': timestamp,
    'KALSHI-ACCESS-SIGNATURE': signature,
  };
}
